import React, { useEffect, useState } from "react";
import { useParams } from 'react-router';
import { containerMiddlewareAdEPortoEnergy } from "../../../../server/constants.api";
import useFetch from "../../../../server/useFetch";
import { useTranslation } from "react-i18next";
import { Row, Col, Flex } from 'antd';
import { Text } from "../../Atomics/Text";
import { NewWidget } from "../../Atomics/NewWidget";
import { isEmpty } from "../../../../utils/hooks/isEmpty";
import { convertTimestampToDayAndMonth, convertTimestampToMonthLong } from "../../../../utils/hooks/convertTimestamp";
import { unitConverterArrays, unitConvertSingleValue } from "../../../../utils/hooks/unitConverter";
import { alignKeys } from "../../../../utils/hooks/alignKeys";

export const ElectricEnergy = ({ dataToFilter, hasActiveEnergy, hasInductiveEnergy, hasCapacitiveEnergy }) => {
  const { t } = useTranslation();
  const { id } = useParams();

  const [metricChart, setMetricChart] = useState('Active Energy');
  const [year, setYear] = useState(dataToFilter?.startDate?.getFullYear());

  const { data: activeEnergyHistoric, isFetching: activeEnergyHistoricIsFetching, refetch: refetchActiveEnergyHistoric } = useFetch(
    "details/ACTIVE_ENERGY", "getDetailsActiveEnergyHistoric",
    {
      id: id,
      startDate: dataToFilter?.startDate?.toISOString()?.split('.')[0],
      endDate: dataToFilter?.endDate?.toISOString()?.split('.')[0],
      periodicity: dataToFilter?.periodicity?.toUpperCase(),
    },
    null, null, containerMiddlewareAdEPortoEnergy);

  const { data: inductiveReactiveEnergyHistoric, isFetching: inductiveReactiveEnergyHistoricIsFetching, refetch: refetchInductiveReactiveEnergyHistoric } = useFetch(
    "details/INDUCTIVE_REACTIVE_ENERGY", "getDetailsInductiveReactiveEnergyHistoric",
    {
      id: id,
      startDate: dataToFilter?.startDate?.toISOString()?.split('.')[0],
      endDate: dataToFilter?.endDate?.toISOString()?.split('.')[0],
      periodicity: dataToFilter?.periodicity?.toUpperCase(),
    },
    null, null, containerMiddlewareAdEPortoEnergy);

  const { data: capacitiveReactiveEnergyHistoric, isFetching: capacitiveReactiveEnergyHistoricIsFetching, refetch: refetchCapacitiveReactiveEnergyHistoric } = useFetch(
    "details/CAPACITIVE_REACTIVE_ENERGY", "getDetailsCapacitiveReactiveEnergyHistoric",
    {
      id: id,
      startDate: dataToFilter?.startDate?.toISOString()?.split('.')[0],
      endDate: dataToFilter?.endDate?.toISOString()?.split('.')[0],
      periodicity: dataToFilter?.periodicity?.toUpperCase(),
    },
    null, null, containerMiddlewareAdEPortoEnergy);

  useEffect(() => {
    setYear(dataToFilter?.startDate?.getFullYear());
    refetchActiveEnergyHistoric();
    refetchInductiveReactiveEnergyHistoric();
    refetchCapacitiveReactiveEnergyHistoric();
  }, [dataToFilter]);

  useEffect(()=> {
    if (activeEnergyHistoric?.data) {
      hasActiveEnergy(activeEnergyHistoric?.data);
    }
    if (inductiveReactiveEnergyHistoric?.data) {
      hasInductiveEnergy(inductiveReactiveEnergyHistoric?.data);
    }
    if (capacitiveReactiveEnergyHistoric?.data) {
      hasCapacitiveEnergy(capacitiveReactiveEnergyHistoric?.data);
    }
  }, [activeEnergyHistoric?.data, inductiveReactiveEnergyHistoric?.data, capacitiveReactiveEnergyHistoric?.data]);

  return (
    <Row id="Energy" gutter={[16, 16]}>
      <Col span={24}>
        <Text $fontColor='coolgray-12' $fontSize='text_xl' $weight='regular' $marginRight='s03'>{t('eletricEnergy')}</Text>
      </Col>
      <Col span={6}>
        <Flex gap={16} vertical='true'>
          <NewWidget data={{
            background: 'white', title: t('activeEnergyConsumption'), icon: 'Bolt',
            charts: [{
              height: '94px',
              chartType: 'bigNumberChart',
              chartId: 'bigNumberChartOne',
              isFetching: activeEnergyHistoricIsFetching,
              data: activeEnergyHistoric?.data && !isEmpty(activeEnergyHistoric?.data?.data) ? getBigNumber(activeEnergyHistoric?.data, year) : null
            }]
          }} />
          <NewWidget data={{
            background: 'white', title: t('inductiveEnergyConsumption'), icon: 'Bolt',
            charts: [{
              height: '94px',
              chartType: 'bigNumberChart',
              chartId: 'bigNumberChartOne',
              isFetching: inductiveReactiveEnergyHistoricIsFetching,
              data: inductiveReactiveEnergyHistoric?.data && !isEmpty(inductiveReactiveEnergyHistoric?.data?.data) ? getBigNumber(inductiveReactiveEnergyHistoric?.data, year) : null
            }]
          }} />
          <NewWidget data={{
            background: 'white', title: t('capacitiveEnergyConsumption'), icon: 'Bolt',
            charts: [{
              height: '94px',
              chartType: 'bigNumberChart',
              chartId: 'bigNumberChartOne',
              isFetching: capacitiveReactiveEnergyHistoricIsFetching,
              data: capacitiveReactiveEnergyHistoric?.data && !isEmpty(capacitiveReactiveEnergyHistoric?.data?.data) ? getBigNumber(capacitiveReactiveEnergyHistoric?.data, year) : null
            }]
          }} />
        </Flex>
      </Col>
      <Col span={18}>
        <NewWidget data={{
          background: 'white',
          title: t('Energy'),
          icon: 'Bolt',
          charts: [{
            height: '422px',
            chartType: 'mixedLineColumnChartApex',
            chartId: 'mixedLineColumnChartApexOne',
            isFetching: metricChart === 'Active Energy' ? activeEnergyHistoricIsFetching : metricChart === 'Inductive Energy' ?
              inductiveReactiveEnergyHistoricIsFetching : capacitiveReactiveEnergyHistoricIsFetching,
            data: metricChart === 'Active Energy' ?
              (activeEnergyHistoric?.data && !isEmpty(activeEnergyHistoric?.data) ?
                dataToFilter?.periodicity === 'Monthly' ? getSerializedMonthlyEnergyHistoric(activeEnergyHistoric?.data, year) :
                  dataToFilter?.periodicity === 'Daily' ? getSerializedDailyEnergyHistoric(activeEnergyHistoric?.data, t, year) :
                    null : null) :
              metricChart === 'Inductive Energy' ? (inductiveReactiveEnergyHistoric?.data && !isEmpty(inductiveReactiveEnergyHistoric?.data) ?
                dataToFilter?.periodicity === 'Monthly' ? getSerializedMonthlyEnergyHistoric(inductiveReactiveEnergyHistoric?.data, year) :
                  dataToFilter?.periodicity === 'Daily' ? getSerializedDailyEnergyHistoric(inductiveReactiveEnergyHistoric?.data, t, year) :
                    null : null) :
                (capacitiveReactiveEnergyHistoric?.data && !isEmpty(capacitiveReactiveEnergyHistoric?.data) ?
                  dataToFilter?.periodicity === 'Monthly' ? getSerializedMonthlyEnergyHistoric(capacitiveReactiveEnergyHistoric?.data, year) :
                    dataToFilter?.periodicity === 'Daily' ? getSerializedDailyEnergyHistoric(capacitiveReactiveEnergyHistoric?.data, t, year) :
                      null : null),
          }],
          buttons: [
            {
              key: "Active Energy",
              title: t("activeEnergy"),
              onButtonClick: (value, key) => setMetricChart(key),
              isActive: metricChart === "Active Energy",
            },
            {
              key: "Inductive Energy",
              title: t("inductiveReactiveEnergy"),
              onButtonClick: (value, key) => setMetricChart(key),
              isActive: metricChart === "Inductive Energy",
            },
            {
              key: "Capacitive Energy",
              title: t("capacitiveReactiveEnergy"),
              onButtonClick: (value, key) => setMetricChart(key),
              isActive: metricChart === "Capacitive Energy",
            }
          ],
        }} />
      </Col>
    </Row>

  )
};

const getBigNumber = (data, year) => {
  const sum = data?.data && !isEmpty(data?.data) ? Object.values(data?.data)
    .filter(value => value !== null) // filtra os valores que não são null
    .reduce((acc, curr) => acc + curr, 0) : 0;
  const { result, newUnit } = unitConvertSingleValue(sum, data?.average?.unit);

  return {
    number: result, unit: newUnit, icon: "Bolt", fluctuation: data?.average?.fluctuation && !isNaN(data?.average?.fluctuation) && Number.isFinite(data?.average?.fluctuation) ? data.average.fluctuation.toFixed(1) : null,
    fluctuationText: data.fluctuation ? year - 1 : null
  };
};

const getSerializedMonthlyEnergyHistoric = (data, year) => {
  const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const convertedData = convertTimestampToMonthLong(data?.data);
  const convertedHomologousData = convertTimestampToMonthLong(data?.homologousData);

  const series = [{
    name: year,
    data: Object.values(convertedData),
    type: 'column'
  }, {
    name: year - 1,
    data: Object.values(convertedHomologousData),
    type: 'column'
  }];

  //Convert unit
  const { convertedResult, newUnit } = unitConverterArrays(series?.map(x => x.data.map(y => y)), data?.average?.unit);
  const convertedSeries = series?.map((x, index) => {
    return {
      ...x,
      data: convertedResult[index]
    }
  });

  return {
    "series": convertedSeries,
    "categories": [],
    "labels": months,
    "xAxisType": null,
    "tooltip": null,
    "unit": newUnit,
    "columnWidth": 20,
    "xAxisLabel": null,
    "yAxisLabel": newUnit,
    yAxisLabelRotate: -90,
    "color": null,
    "filename": "Gráfico exportado",
    "headerValue": "valores",
    "headerCategory": "categorias",
    "showTooltipTitle": true
  };
};

const getSerializedDailyEnergyHistoric = (data, t, year) => {
  const convertedData = convertTimestampToDayAndMonth(data?.data);
  const convertedHomologousData = convertTimestampToDayAndMonth(data?.homologousData);

  const { result, keys } = alignKeys('dd/mm', { [year]: convertedData }, { [year - 1]: convertedHomologousData });

  const series = [];
  result?.map((item, index) => {
    const values = Object.values(item)[0];
    const data = Object.entries(values)?.map(([key, value]) => { return value });

    series.push({
      name: t(Object.keys(item)[0]),//TODO: Add Translations
      type: 'column',
      data: data
    })
  });

  //Convert unit
  const { convertedResult, newUnit } = unitConverterArrays(series?.map(x => x.data.map(y => y)), data?.average?.unit);
  const convertedSeries = series?.map((x, index) => {
    return {
      ...x,
      data: convertedResult[index]
    }
  });

  return {
    "series": convertedSeries,
    "categories": [],
    "labels": keys,
    "xAxisType": null,
    "tooltip": null,
    "unit": newUnit,
    "columnWidth": 20,
    "xAxisLabel": null,
    "yAxisLabel": newUnit,
    yAxisLabelRotate: -90,
    "color": null,
    tickAmount: 12,
    "filename": "Gráfico exportado",
    "headerValue": "valores",
    "headerCategory": "categorias",
    "showTooltipTitle": true
  };
};